import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'

export interface School {
  id: string | null
  schulenId: string | null
  /** dim.School.school_id — the BIGINT PK used as FK in ops.* tables (e.g. RefoodContract).
   *  Use this, NOT `id` (which is FM_SchoolId), when writing to any ops.* FK column. */
  dimSchoolId: number | null
  name: string
  schulnummer: string | null
  firma: string | null
  bundesland: string | null
  stadt: string | null
  plz: string | null
  adresse: string | null
  lat: string | null
  lon: string | null
  projektleiter: string | null
  kinderkategorie: string | null
  abkuerzung: string | null
  maxStufe: string | null
  mandantId: string | null
  schuleLoginName: string | null
  status: string | null
  syncedAt: string
  inaktivSetAm: string | null
  inaktivSetBy: string | null
  /** Canonical Kostenstelle for this school — sourced from `dim.Kostenstelle` (migration 069 + 092). */
  kostenstelleId: number | null
  /** Display value: prefers CEO-curated `display_code`, falls back to machine `code` (e.g. `SCH_86`). Unique per school. */
  kostenstelleCode: string | null
  /** Raw CEO-curated override, NULL if not yet set. Used by the Stammdaten admin form's edit field. */
  kostenstelleDisplayCode: string | null
  /** Machine-generated stable code (e.g. `SCH_86`), shown as a gray hint when no display_code is set. */
  kostenstelleMachineCode: string | null
  /** Display label from `dim.Kostenstelle.name`. */
  kostenstelleName: string | null
  /** Linked self-hosted tour embed URL (ops.SchoolTour) — read-only JOIN field. */
  tourEmbedUrl: string | null
  /** Friendly name of the linked tour, captured at link time. */
  tourName: string | null
}

// kostenstelle* fields are read-only — sourced from a JOIN on dim.Kostenstelle
// (migration 069/092) at SELECT time. The schools admin form never sets them
// directly; they're managed via the Kostenstelle admin UI (Phase 1.5).
export type SchoolInput = Omit<
  School,
  'id' | 'schulenId' | 'syncedAt' | 'inaktivSetAm' | 'inaktivSetBy'
       | 'kostenstelleId' | 'kostenstelleCode' | 'kostenstelleDisplayCode'
       | 'kostenstelleMachineCode' | 'kostenstelleName'
       | 'dimSchoolId'  // read-only JOIN field, derived from dim.School
       | 'tourEmbedUrl' | 'tourName'  // read-only JOIN, linked via the 3D tab
> & {
  inaktivSetAm?: string | null
  inaktivSetBy?: string | null
}

export function useSchulen() {
  return useQuery({
    queryKey: ['schulen'],
    // Client-side boundary normalization (belt-and-braces to the server-side
    // toRow() fix): the mssql driver returns BIGINT as STRINGS, and a stale
    // /api/schulen payload shipping "106" instead of 106 silently zeroes every
    // strict numeric match downstream (DB-Kostenart Auswertung, the
    // SchoolKostenartPanel, PcaExplorer Projektleitung). Number() here makes
    // every consumer type-safe regardless of what the wire delivers.
    queryFn: async () => {
      const rows = await api.get<School[]>('/api/schulen')
      return rows.map((s) => ({
        ...s,
        dimSchoolId: s.dimSchoolId != null ? Number(s.dimSchoolId) : null,
        kostenstelleId: s.kostenstelleId != null ? Number(s.kostenstelleId) : null,
      }))
    },
    staleTime: 5 * 60_000,
  })
}

export function useCreateSchool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SchoolInput) => api.post<School>('/api/schulen', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schulen'] }),
  })
}

export function useUpdateSchool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SchoolInput }) =>
      api.put<School>(`/api/schulen/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schulen'] }),
  })
}

export function useDeleteSchool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/api/schulen/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schulen'] }),
  })
}

export interface HamburgLookupResult {
  name: string | null
  schulnummer: string | null
  adresse: string | null
  plz: string | null
  stadt: string | null
  bundesland: string
  lat: string | null
  lon: string | null
  kinderkategorie: string | null
  schulform: string | null
  rechtsform: string | null
  bezirk: string | null
  stadtteil: string | null
  source: string
}

export function useHamburgLookup() {
  return useMutation({
    mutationFn: (schulnummer: string) =>
      api.get<HamburgLookupResult>(`/api/hamburg-schule?schulnummer=${encodeURIComponent(schulnummer)}`),
  })
}

export function useHamburgSearch(query: string) {
  return useQuery({
    queryKey: ['hamburg-search', query],
    queryFn: () => api.get<HamburgLookupResult[]>(`/api/hamburg-schule/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length >= 2,
    staleTime: 5 * 60_000,
  })
}

// ── PCA-Abgleich (/schulen → button) ─────────────────────────────────────────
export interface PcaSchulenSyncRow {
  agencyId:   string
  name:       string
  shortName:  string
  vertrag:    9701 | 125103 | null
  state:      'linked' | 'match' | 'new'
  fmSchoolId: string | null
  schoolName: string | null
}
export interface PcaSchulenSyncPreview {
  rows:   PcaSchulenSyncRow[]
  counts: { linked: number; match: number; new: number }
}
export function usePcaSchulenSyncPreview(enabled: boolean) {
  return useQuery({
    queryKey: ['schulen', 'pca-sync-preview'],
    queryFn:  () => api.get<PcaSchulenSyncPreview>('/api/pca/schulen/sync'),
    enabled,
    staleTime: 0,
  })
}
export function usePcaSchulenSyncApply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { link: { agencyId: string; fmSchoolId: string }[]; create: string[] }) =>
      api.post<{ linked: number; created: number; backfilled: number }>('/api/pca/schulen/sync', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schulen'] })
      qc.invalidateQueries({ queryKey: ['pca'] })
    },
  })
}
